import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { CodeBlock } from '@/components/app/CodeBlock';
import { LoadingState } from '@/components/app/LoadingState';
import { ErrorState } from '@/components/app/ErrorState';
import { SeverityBadge } from '../components/SeverityBadge';
import { useAcknowledgeFiring, useAlertFirings } from '../hooks';
import { humanizeError } from '@/lib/apiClient';

export function FiringDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // We don't have a single-firing GET endpoint; fetch the recent list and
  // scan it. Firings page hits the same URL so the row is in cache already.
  const { data, isLoading, isError, error, refetch } = useAlertFirings({ limit: 200 });
  const ackMut = useAcknowledgeFiring();
  const [note, setNote] = React.useState('');
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const firing = (data?.firings ?? []).find((f) => f.firingId === id);

  if (isLoading) return <LoadingState label="Loading firing" />;
  if (isError)
    return (
      <ErrorState
        title="Firing unavailable"
        description={humanizeError(error)}
        actionLabel="Retry"
        onAction={() => refetch()}
      />
    );
  if (!firing)
    return (
      <ErrorState
        title="Firing not found"
        description={`No firing with id "${id}" in the recent window.`}
        actionLabel="Back to firings"
        actionHref="/notifications/alerts/firings"
      />
    );

  const handleAck = async () => {
    setSubmitError(null);
    try {
      await ackMut.mutateAsync({ firingId: firing.firingId, note: note.trim() || undefined });
      navigate('/notifications/alerts/firings');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Ack failed');
    }
  };

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate('/notifications/alerts/firings')}>
        <ArrowLeft className="h-4 w-4" /> Back to firings
      </Button>

      <PageHeader
        title={`Firing ${firing.firingId}`}
        description={firing.policyKey}
        actions={
          <div className="flex items-center gap-2">
            <SeverityBadge severity={firing.severity} />
            {firing.resolvedAt && <Badge variant="success">Resolved</Badge>}
            {firing.acknowledgedAt && !firing.resolvedAt && <Badge variant="muted">Ack</Badge>}
          </div>
        }
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Observation</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3 text-sm">
            <KeyValue label="Fired at" value={firing.firedAt} mono />
            <KeyValue label="Resolved at" value={firing.resolvedAt ?? '—'} mono />
            <KeyValue label="Threshold" value={String(firing.threshold)} mono />
            <KeyValue label="Observed" value={String(firing.observedValue)} mono />
            <KeyValue
              label="Dispatched"
              value={firing.dispatchedTo.join(', ') || '—'}
            />
            {firing.dispatchFailures && firing.dispatchFailures.length > 0 && (
              <KeyValue
                label="Dispatch failures"
                value={firing.dispatchFailures.map((f) => `${f.channel}: ${f.error}`).join('; ')}
                tone="destructive"
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Acknowledge</CardTitle>
            <CardDescription>
              Ack quiets this firing for the cooldown. Writes to audit under the firing id.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {firing.acknowledgedAt ? (
              <div className="text-sm">
                Ack by <span className="font-mono">{firing.acknowledgedBy ?? 'unknown'}</span> at {firing.acknowledgedAt}
                {firing.acknowledgeNote && <div className="mt-1 text-muted-foreground">"{firing.acknowledgeNote}"</div>}
              </div>
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="ack-note">Note (optional)</Label>
                  <Textarea
                    id="ack-note"
                    rows={3}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. Investigating — suspect a false positive"
                    maxLength={500}
                  />
                </div>
                {submitError && <p className="text-sm text-destructive">{submitError}</p>}
                <Button onClick={handleAck} disabled={ackMut.isPending}>
                  <CheckCircle2 className="h-4 w-4" />
                  {ackMut.isPending ? 'Acknowledging…' : 'Acknowledge'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Snapshot</CardTitle>
          <CardDescription>Subtree captured at fire time for postmortem.</CardDescription>
        </CardHeader>
        <CardContent>
          <CodeBlock value={firing.snapshot} />
        </CardContent>
      </Card>
    </div>
  );
}

function KeyValue({
  label,
  value,
  mono,
  tone,
}: {
  label: string;
  value: string;
  mono?: boolean;
  tone?: 'destructive';
}) {
  return (
    <div>
      <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
      <div
        className={[
          mono ? 'font-mono text-xs' : 'text-sm',
          tone === 'destructive' ? 'text-destructive' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {value}
      </div>
    </div>
  );
}
